import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import VasePreview from '../common/VasePreview';
import { sendEmail, emailTemplates, ADMIN_EMAIL } from '../../lib/emailService';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { DELIVERY_FEE, FREE_DELIVERY_THRESHOLD, TAX_RATE } from '../../lib/pricing';

// Load Stripe outside of component render to avoid recreating on each render
const stripePromise = loadStripe('pk_live_51TK90BB0N8J84KuU0MtnxTbK9xN3dTRD25TCtMBPa2mNLdNLZXextYNLgB6LtbAAmciJ4vartrBdMOyykc32aaHg00Flpeqp3m');

const IMG_BLANK = '/images/smilevase-blank-card.png';

// ─── Card Preview (matches Step2Addons preview exactly) ───────────────────────
function CardPreview({ cardFromName, stickerImageUrl }) {
  const [imgError, setImgError] = React.useState(false);
  return (
    <div className="bg-warmwhite p-3 rounded-2xl border border-sage-100 shadow-soft inline-block">
      <div className="relative w-[120px] shadow-lg rounded-sm overflow-hidden bg-white aspect-[3/4]">
        <img src={IMG_BLANK} alt="Gift card" className="w-full h-full object-cover block" />
        {stickerImageUrl && !imgError && (
          <div
            className="absolute flex items-center justify-center"
            style={{ top: '10%', left: '50%', transform: 'translateX(-50%)', width: '60%', height: '45%' }}
          >
            <img
              src={stickerImageUrl}
              alt="Sticker"
              className="w-full h-full object-contain drop-shadow-md"
              onError={() => setImgError(true)}
            />
          </div>
        )}
        {cardFromName && (
          <div
            className="absolute font-serif font-bold text-charcoal w-full text-center px-2 leading-tight break-words"
            style={{ bottom: '10%', fontSize: cardFromName.length > 14 ? '0.6rem' : '0.9rem' }}
          >
            {cardFromName}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Stripe Payment Form ───────────────────────────────────────────────────
function PaymentForm({ onSuccess, onBack, isSubmitting, setIsSubmitting, setErrorMsg, orderPayload, session, grandTotal, taxAmount, deliveryCharge, formData }) {
  const stripe = useStripe();
  const elements = useElements();

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setErrorMsg('');

    try {
      // Confirm the payment — card is charged immediately
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Payment authorized — now create the account + order in Supabase
      await onSuccess();

    } catch (err) {
      console.error('Payment error:', err);
      setErrorMsg(err.message || 'Payment failed. Please check your card details and try again.');
      setIsSubmitting(false);
    }
  };

  const stripeAppearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#e8a4b8',
      colorBackground: '#ffffff',
      colorText: '#2c3e38',
      colorDanger: '#ef4444',
      fontFamily: 'Inter, sans-serif',
      borderRadius: '16px',
      spacingUnit: '4px',
    },
    rules: {
      '.Input': {
        border: '1px solid #e3e8e1',
        boxShadow: 'none',
        padding: '12px 16px',
      },
      '.Input:focus': {
        border: '1px solid #e8a4b8',
        boxShadow: '0 0 0 3px rgba(232, 164, 184, 0.15)',
      },
      '.Label': {
        fontWeight: '600',
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: '#6b8262',
      },
    },
  };

  return (
    <form onSubmit={handlePay} className="space-y-6">
      {/* Order summary */}
      <div className="bg-sage-50/50 rounded-2xl p-5 border border-sage-100 space-y-3">
        <h4 className="font-serif text-lg text-charcoal">Order Summary</h4>
        <div className="flex justify-between text-sm">
          <span className="text-sage-500">{formData.vase_size?.name || '24 Roses'} — {formData.design?.name}</span>
          <span className="font-bold text-charcoal">${formData.vase_size?.price || 0}</span>
        </div>
        {(formData.addons || []).map(addon => (
          <div key={addon.id} className="flex justify-between text-sm">
            <span className="text-sage-500">{addon.name}</span>
            <span className="font-bold text-charcoal">${addon.price}</span>
          </div>
        ))}
        <div className="flex justify-between text-sm">
          <span className="text-sage-500">Tax (9.1% AZ TPT)</span>
          <span className="font-bold text-charcoal">${taxAmount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-sage-500">Delivery</span>
          <span className="font-bold">
            {deliveryCharge === 0
              ? <span className="text-green-600">FREE</span>
              : <span className="text-charcoal">${DELIVERY_FEE}</span>}
          </span>
        </div>
        <div className="flex justify-between pt-3 border-t border-sage-100">
          <span className="font-serif text-lg text-charcoal">Total</span>
          <span className="font-serif text-2xl text-charcoal">${grandTotal.toFixed(2)}</span>
        </div>
        <p className="text-[10px] text-sage-400 font-medium">
          Your card will be authorized but <strong>not charged</strong> until we review and approve your order.
        </p>
      </div>

      {/* Stripe payment element */}
      <div className="bg-white rounded-2xl border border-sage-100 p-5">
        <PaymentElement />
      </div>

      <div className="flex flex-col-reverse md:flex-row justify-between gap-4 pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="btn-secondary md:w-auto min-h-[56px] text-lg"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !stripe}
          className="btn-primary md:w-auto md:px-12 flex items-center justify-center gap-3 min-h-[56px] text-lg"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              Processing...
            </>
          ) : (
            <>
              <SafeIcon name="Lock" />
              Pay ${grandTotal.toFixed(2)}
            </>
          )}
        </button>
      </div>

      <p className="text-center text-[10px] text-sage-400 font-medium flex items-center justify-center gap-1">
        <SafeIcon name="Lock" className="text-xs" />
        Secured by Stripe. Your card details are never stored on our servers.
      </p>
    </form>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function Step4Review({ formData, onBack, setStep, session, onOrderComplete }) {
  const [subStep, setSubStep] = useState('review'); // 'review' | 'payment'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [loadingPayment, setLoadingPayment] = useState(false);

  const isLoggedIn = !!session;
  const productName = formData.vase_size?.name || '24 Roses';
  const colorDisplay = formData.flower_type;
  const addonsTotal = (formData.addons || []).reduce((sum, a) => sum + a.price, 0);
  const subtotal = (formData.vase_size?.price || 0) + addonsTotal;
  const taxAmount = Math.round(subtotal * TAX_RATE * 100) / 100;
  const deliveryCharge = subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_FEE;
  const grandTotal = Math.round((subtotal + taxAmount + deliveryCharge) * 100) / 100;

  // When customer clicks "Continue to Payment" on the review screen,
  // create the account first then get the PaymentIntent client secret
  const handleProceedToPayment = async () => {
    setLoadingPayment(true);
    setErrorMsg('');
    setAlreadyRegistered(false);

    try {
      let authUserId = null;

      if (isSupabaseConfigured()) {
        if (isLoggedIn) {
          authUserId = session.user.id;
          await supabase.from('profiles').update({
            full_name: formData.customer_name,
            phone: formData.customer_phone,
          }).eq('id', authUserId);

        } else {
          const { data: authData, error: authError } = await supabase.auth.signUp({
            email: formData.customer_email,
            password: formData.password,
            options: { data: { full_name: formData.customer_name } }
          });

          if (authError) {
            if (
              authError.message?.toLowerCase().includes('already registered') ||
              authError.message?.toLowerCase().includes('already been registered') ||
              authError.status === 422
            ) {
              setAlreadyRegistered(true);
              setLoadingPayment(false);
              return;
            }
            throw authError;
          }

          authUserId = authData.user?.id;
          if (!authUserId) throw new Error('Account creation failed. Please try again.');

          const { error: profileError } = await supabase.from('profiles').upsert({
            id: authUserId,
            full_name: formData.customer_name,
            email: formData.customer_email,
            phone: formData.customer_phone,
          });

          if (profileError) throw new Error('Failed to create your account. Please try again.');
        }

        // Create PaymentIntent via Edge Function — capture immediately on confirm
        const { data, error } = await supabase.functions.invoke('create-payment-intent', {
          body: {
            amount: grandTotal,
            customerEmail: formData.customer_email,
            customerName: formData.customer_name,
            description: `${productName} - ${formData.design?.name || 'Custom'} - SmileVase`,
            capture_method: 'automatic', // Charge card immediately on confirm
          }
        });

        if (error) throw new Error(error.message || 'Failed to initialize payment.');
        if (!data?.clientSecret) throw new Error('No client secret returned from payment service.');

        setClientSecret(data.clientSecret);
        setPaymentIntentId(data.paymentIntentId);

        // Store authUserId for use after payment
        formData._authUserId = authUserId;

        setSubStep('payment');

      } else {
        // Mock mode — skip to payment UI
        setClientSecret('mock_secret');
        setSubStep('payment');
      }

    } catch (err) {
      console.error('Proceed to payment error:', err);
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoadingPayment(false);
    }
  };

  // Called after Stripe confirms the card authorization
  const handlePaymentSuccess = async () => {
    setIsSubmitting(true);
    try {
      const authUserId = formData._authUserId || session?.user?.id;

      // Insert Order with payment intent ID and paid status
      const { data: newOrder, error: orderError } = await supabase
        .from('orders')
        .insert({
          customer_id: authUserId,
          customer_name: formData.customer_name,
          customer_email: formData.customer_email,
          customer_phone: formData.customer_phone,
          occasion: formData.occasion,
          delivery_date: formData.delivery_date,
          delivery_type: formData.delivery_type || 'scheduled',
          delivery_address: formData.delivery_address,
          delivery_city: formData.delivery_city,
          delivery_zip: formData.delivery_zip,
          recipient_name: formData.recipient_name,
          recipient_phone: formData.recipient_phone,
          special_instructions: formData.special_instructions,
          stripe_payment_intent_id: paymentIntentId,
          stripe_amount_cents: Math.round(grandTotal * 100),
          tax_amount_cents: Math.round(taxAmount * 100),
          status: 'paid',
        })
        .select()
        .single();

      if (orderError) throw orderError;
      setOrderId(newOrder.id);

      // Insert Order Item
      const { data: newItem, error: itemError } = await supabase
        .from('order_items')
        .insert({
          order_id: newOrder.id,
          product_name: productName,
          size: formData.vase_size?.name,
          color_preference: colorDisplay,
          quantity: 1,
          price: formData.vase_size?.price,
          design_id: formData.design?.id,
          design_name: formData.design?.name,
          design_image_url: formData.design?.image_url,
          color_preference: formData.flower_type,
          addons: formData.addons?.length > 0 ? JSON.stringify(formData.addons) : null,
          card_from_name: formData.card_from_name || null,
          card_design: formData.card_design || null,
          card_design_image_url: formData.card_design_image_url || null,
          balloon_color: formData.balloon_color || null,
        })
        .select()
        .single();

      if (itemError) throw itemError;

      // Send customer confirmation email
      await sendEmail(
        formData.customer_email,
        "Your order is confirmed — SmileVase",
        emailTemplates.orderReceived(newOrder, newItem)
      );

      // Send admin notification (fire-and-forget — don't block success screen)
      sendEmail(
        ADMIN_EMAIL,
        `New Order — #${newOrder.id.substring(0, 8)} from ${formData.customer_name}`,
        emailTemplates.adminNewOrder(newOrder, newItem)
      ).catch(err => console.error('Admin notification failed:', err));

      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#e8a4b8', '#c7607e', '#6b8262', '#ffffff']
      });
      if (onOrderComplete) onOrderComplete();
      setIsSuccess(true);
      setTimeout(() => {
        window.location.href = 'https://smilevase.com/thank-you';
      }, 9000);

    } catch (err) {
      console.error('Order creation error:', err);
      setErrorMsg(err.message || 'Order could not be saved. Please contact us at orders@smilevase.com.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Success Screen ─────────────────────────────────────────────────────
  if (isSuccess) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12 space-y-6">
        <div className="w-24 h-24 bg-blush-100 rounded-full flex items-center justify-center mx-auto">
          <SafeIcon name="Check" className="text-5xl text-blush-400" />
        </div>
        <h2 className="text-4xl font-serif text-charcoal">Order Placed!</h2>
        <div className="space-y-1">
          <p className="text-sage-500 text-lg">Thank you, {formData.customer_name?.split(' ')[0]}.</p>
          <p className="text-blush-400 font-bold tracking-widest text-sm">ORDER ID: {orderId.substring(0, 8)}</p>
        </div>
        <div className="glass-panel p-6 max-w-sm mx-auto text-left space-y-4 mt-4">
          <div className="bg-green-50 border border-green-100 rounded-2xl p-4 flex items-center gap-3">
            <SafeIcon name="CheckCircle" className="text-green-500 shrink-0 text-xl" />
            <div>
              <p className="text-sm font-bold text-green-700">Payment confirmed</p>
              <p className="text-xs text-green-600">Your card has been charged ${grandTotal.toFixed(2)}. A confirmation email is on its way.</p>
            </div>
          </div>
          <h3 className="font-serif text-lg border-b border-sage-100 pb-2">What happens next?</h3>
          <ul className="space-y-3 text-sm text-charcoal">
            <li className="flex gap-3">
              <SafeIcon name="Camera" className="text-blush-300 shrink-0 mt-0.5" />
              We'll craft your arrangement and send you a photo before delivery.
            </li>
            <li className="flex gap-3">
              <SafeIcon name="Truck" className="text-blush-300 shrink-0 mt-0.5" />
              We hand deliver on your chosen date.
            </li>
          </ul>
          <button
            onClick={() => window.location.hash = '#/portal'}
            className="btn-primary w-full flex items-center justify-center gap-3 mt-4 shadow-glow"
          >
            Track Your Order
            <SafeIcon name="ArrowRight" />
          </button>
        </div>
      </motion.div>
    );
  }

  // ── Payment Step ───────────────────────────────────────────────────────
  if (subStep === 'payment' && clientSecret && clientSecret !== 'mock_secret') {
    return (
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-serif text-charcoal">Secure Payment</h2>
          <p className="text-sage-500">Your card will be charged when you confirm your order.</p>
        </div>

        {errorMsg && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-100 text-sm text-center">
            {errorMsg}
          </div>
        )}

        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#e8a4b8',
                colorBackground: '#ffffff',
                colorText: '#2c3e38',
                fontFamily: 'Inter, sans-serif',
                borderRadius: '16px',
              },
              rules: {
                '.Input': { border: '1px solid #e3e8e1', padding: '12px 16px' },
                '.Input:focus': { border: '1px solid #e8a4b8', boxShadow: '0 0 0 3px rgba(232,164,184,0.15)' },
                '.Label': { fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b8262' },
              }
            }
          }}
        >
          <PaymentForm
            onSuccess={handlePaymentSuccess}
            onBack={() => setSubStep('review')}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
            setErrorMsg={setErrorMsg}
            grandTotal={grandTotal}
            taxAmount={taxAmount}
            deliveryCharge={deliveryCharge}
            formData={formData}
          />
        </Elements>
      </motion.div>
    );
  }

  // ── Review Step ────────────────────────────────────────────────────────
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-serif text-charcoal">Review Your Order</h2>
        <p className="text-sage-500">Double check the details before we get to work.</p>
      </div>

      {alreadyRegistered && (
        <div className="bg-blush-50 border border-blush-100 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-3">
            <SafeIcon name="AlertCircle" className="text-blush-400 text-xl shrink-0" />
            <p className="text-sm font-semibold text-charcoal">An account with this email already exists.</p>
          </div>
          <p className="text-sm text-sage-500 pl-8">
            Please{' '}
            <a href="#/portal" className="text-blush-400 font-bold hover:text-blush-500 transition-colors">
              sign in to your account
            </a>
            {' '}to place a new order, or use a different email address.
          </p>
        </div>
      )}

      {errorMsg && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-100 text-sm text-center">
          {errorMsg}
        </div>
      )}

      <div className="glass-panel overflow-hidden">

        {/* ── Hero: vase + addons + card ── */}
        <div className="p-6 md:p-8 border-b border-sage-100 bg-sage-50/30">
          <div className="flex flex-col sm:flex-row gap-6 items-start">

            {/* Vase — dominant hero */}
            <div className="flex flex-col items-center gap-3 shrink-0">
              <div className="relative">
                <div className="w-52 md:w-56 shadow-glow ring-4 ring-white rounded-[2.5rem] overflow-hidden">
                  <VasePreview design={formData.design} roseColor={formData.flower_type} />
                </div>
                <div className="absolute -bottom-2 -right-2 bg-blush-300 text-white p-2.5 rounded-2xl shadow-lg">
                  <SafeIcon name="Gift" className="text-lg" />
                </div>
              </div>
              <div className="text-center space-y-1">
                <h4 className="font-serif text-xl text-charcoal leading-tight">{formData.design?.name}</h4>
                <div className="flex flex-wrap justify-center gap-1.5">
                  <span className="bg-sage-50 border border-sage-100 text-charcoal px-2.5 py-0.5 rounded-full text-[10px] font-bold">{productName}</span>
                  <span className="bg-blush-50 border border-blush-100 text-blush-500 px-2.5 py-0.5 rounded-full text-[10px] font-bold capitalize">{colorDisplay}</span>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px self-stretch bg-sage-100" />

            {/* Right — add-ons + card stacked */}
            <div className="flex-1 space-y-5 w-full">

              {/* Add-ons */}
              {(formData.addons || []).length > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Perfect Pairings</p>
                    <button onClick={() => setStep('addons')} className="text-sage-400 hover:text-blush-400 text-xs">Edit</button>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    {(formData.addons || []).map((addon) => (
                      <div key={addon.id} className="flex flex-col items-center gap-2" style={{ width: '96px' }}>
                        <div className="w-24 h-24 ring-2 ring-white shadow-md rounded-2xl overflow-hidden bg-warmwhite flex items-center justify-center p-2 shrink-0">
                          <img src={addon.image} alt={addon.name} className="w-full h-full object-contain" />
                        </div>
                        <span className="text-xs font-medium text-sage-500 text-center leading-tight">{addon.name}</span>
                        <span className="text-xs font-bold text-blush-400">${addon.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gift card preview */}
              {formData.card_from_name && (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Gift Card · Included Free</p>
                  <div className="flex items-center gap-4 bg-white rounded-2xl border border-sage-100 p-3">
                    <CardPreview
                      cardFromName={formData.card_from_name}
                      stickerImageUrl={formData.card_design_image_url || ''}
                    />
                    <div className="space-y-1.5">
                      <div>
                        <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Signed by</p>
                        <p className="font-bold text-charcoal text-sm">{formData.card_from_name}</p>
                      </div>
                      {formData.card_design_name && (
                        <div>
                          <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Sticker</p>
                          <p className="font-bold text-charcoal text-sm">{formData.card_design_name}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* If no add-ons and no card, show arrangement details inline */}
              {(formData.addons || []).length === 0 && !formData.card_from_name && (
                <div className="space-y-2 pt-2">
                  <button onClick={() => setStep('1b')} className="text-sage-400 hover:text-blush-400 text-xs">Edit arrangement</button>
                  <div className="space-y-1">
                    <p className="text-sm text-sage-500">Occasion: <span className="font-bold text-charcoal">{formData.occasion}</span></p>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* ── Bottom: order total + delivery side by side ── */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Order total */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h4 className="font-serif font-medium text-charcoal text-lg">Order Summary</h4>
              <button onClick={() => setStep('1b')} className="text-sage-400 hover:text-blush-400 text-sm">Edit</button>
            </div>
            <div className="bg-warmwhite p-5 rounded-2xl border border-sage-100 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-sage-400">{productName}</span>
                <span className="font-medium text-charcoal">${formData.vase_size?.price || 0}</span>
              </div>
              {(formData.addons || []).map(addon => (
                <div key={addon.id} className="flex justify-between text-sm">
                  <span className="text-sage-400">{addon.name}</span>
                  <span className="font-medium text-charcoal">${addon.price}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm pt-2 border-t border-sage-100 mt-1">
                <span className="text-sage-400">Tax (9.1% AZ TPT)</span>
                <span className="font-medium text-charcoal">${taxAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-sage-400">
                  Delivery {deliveryCharge === 0 ? `(free over $${FREE_DELIVERY_THRESHOLD})` : ''}
                </span>
                {deliveryCharge === 0
                  ? <span className="font-medium text-green-600">FREE</span>
                  : <span className="font-medium text-charcoal">${deliveryCharge}</span>}
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-sage-200 mt-1">
                <span className="font-serif text-lg text-charcoal">Total</span>
                <span className="font-serif text-3xl text-charcoal">${grandTotal.toFixed(2)}</span>
              </div>
              <p className="text-[10px] text-sage-400 font-medium pt-1">Charged on confirmation</p>
            </div>
          </div>

          {/* Delivery details */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h4 className="font-serif font-medium text-charcoal text-lg">Delivery</h4>
              <button onClick={() => setStep(2)} className="text-sage-400 hover:text-blush-400 text-sm">Edit</button>
            </div>
            <div className="bg-warmwhite p-5 rounded-2xl border border-sage-100 space-y-4">
              <div>
                <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Recipient</p>
                <p className="font-bold text-charcoal">{formData.recipient_name}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Date</p>
                <p className="font-bold text-charcoal">
                  {formData.delivery_date
                    ? new Date(formData.delivery_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Address</p>
                <p className="font-bold text-charcoal">{formData.delivery_address}</p>
                <p className="text-sm text-sage-500">{formData.delivery_city}, {formData.delivery_zip}</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div className="flex flex-col-reverse md:flex-row justify-between gap-4 pt-4">
        <button onClick={onBack} disabled={loadingPayment} className="btn-secondary md:w-auto min-h-[56px] text-lg">Back</button>
        <button
          onClick={handleProceedToPayment}
          disabled={loadingPayment}
          className="btn-primary md:w-auto md:px-12 flex items-center justify-center gap-3 min-h-[56px] text-lg"
        >
          {loadingPayment ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              Preparing Payment...
            </>
          ) : (
            <>
              Continue to Payment
              <SafeIcon name="ArrowRight" />
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}
